import { action, observable, runInAction } from 'mobx';
import { Container, Inject } from 'typescript-ioc';
import { IRouter } from '../../../infrastructure/router';
import { RouteParams, Routes } from '../../../routes-table';
import { IEventHubDTO, IEventHubsDAO, IEventHubVersionsDTO } from '../../../api/event-hubs.dao';
import { EventHubsDAO } from '../../../api/implementations/event-hubs.dao';
import { IErrorParsing } from '../../../infrastructure/error-parsing';
import { alertServerError } from '../../global/modal/alerts-host/alert-templates';
import { errorText } from '../../../constants/common-labels.constants';
import { getFormattedEventHubUrl } from '../../../infrastructure/utils/common.utils';
import { HubPreviewSyncExpectation, HubPreviewRefreshCallback } from './_content/_hub-embed/hub-preview-sync.types';
import { waitForHubPreviewSync } from './_content/_hub-embed/hub-preview-sync';

export class EventHubWebsiteBuilderVM {
    @observable
    private eventHub: IEventHubDTO | null = null;
    @observable
    private eventHubVersions: IEventHubVersionsDTO | null = null;
    @observable
    private hubEmbedRefreshKey = 0;
    @observable
    private hubEmbedRefreshing = false;
    private hubEmbedRefreshGeneration = 0;

    constructor(
        private readonly accountId: number,
        private readonly eventHubId: number,
        @Inject private readonly router: IRouter<RouteParams> = Container.get(IRouter),
        @Inject private readonly eventHubsDAO: IEventHubsDAO = Container.get(EventHubsDAO),
        @Inject private readonly errorParsingService: IErrorParsing = Container.get(IErrorParsing),
    ) {}

    public async initialize(): Promise<void> {
        await Promise.all([
            this.loadEventHub(),
            this.loadEventHubVersions(),
        ]);
    }

    public getEventHubId(): number {
        return this.eventHubId;
    }

    public getAccountId(): number {
        return this.accountId;
    }

    public getPreviewVersionId(): number | null {
        return this.eventHubVersions?.previewVersionId ?? null;
    }

    public getHubName(): string {
        return this.eventHub?.eventHubName ?? '';
    }

    public getHubUrl(): string | null {
        return this.eventHub?.subdomain ? getFormattedEventHubUrl(this.eventHub.subdomain) : null;
    }

    public getHubEmbedRefreshKey(): number {
        return this.hubEmbedRefreshKey;
    }

    public isHubEmbedRefreshing(): boolean {
        return this.hubEmbedRefreshing;
    }

    public beginHubEmbedLoading = (): number => {
        const generation = ++this.hubEmbedRefreshGeneration;
        runInAction(() => {
            this.hubEmbedRefreshing = true;
        });
        return generation;
    };

    public cancelHubEmbedLoading = (generation: number): void => {
        if (generation !== this.hubEmbedRefreshGeneration) {
            return;
        }

        runInAction(() => {
            this.hubEmbedRefreshing = false;
        });
    };

    /**
     * Keep loading visible (usually already started on Save click), wait for Hub BE,
     * then remount the iframe once. Only the latest generation remounts.
     */
    public refreshHubEmbed = async (
        expectation?: HubPreviewSyncExpectation,
        generation?: number,
    ): Promise<void> => {
        const activeGeneration = generation ?? ++this.hubEmbedRefreshGeneration;

        runInAction(() => {
            this.hubEmbedRefreshing = true;
        });

        try {
            const subdomain = this.eventHub?.subdomain;
            if (subdomain && expectation) {
                await waitForHubPreviewSync(subdomain, expectation);
            }
        } finally {
            if (activeGeneration === this.hubEmbedRefreshGeneration) {
                runInAction(() => {
                    this.hubEmbedRefreshKey += 1;
                    this.hubEmbedRefreshing = false;
                });
            }
        }
    };

    public getHubPreviewRefresh(): HubPreviewRefreshCallback {
        return {
            beginLoading: this.beginHubEmbedLoading,
            refresh: this.refreshHubEmbed,
            cancelLoading: this.cancelHubEmbedLoading,
        };
    }

    public navigateBack = (): void => {
        this.router.navigate(Routes.EventHubConfiguration, {
            accountId: String(this.accountId),
            eventHubId: String(this.eventHubId),
        });
    };

    @action
    private setEventHub(eventHub: IEventHubDTO): void {
        this.eventHub = eventHub;
    }

    @action
    private setEventHubVersions(versions: IEventHubVersionsDTO): void {
        this.eventHubVersions = versions;
    }

    private async loadEventHub(): Promise<void> {
        try {
            const eventHub = await this.eventHubsDAO.getEventHub(this.accountId, this.eventHubId);
            this.setEventHub(eventHub);
        } catch (error) {
            const errorMessage = this.errorParsingService.extractMessage(error);
            alertServerError(errorText(), errorMessage);
        }
    }

    private async loadEventHubVersions(): Promise<void> {
        try {
            const versions = await this.eventHubsDAO.getEventHubVersions(this.eventHubId);
            this.setEventHubVersions(versions);
        } catch (error) {
            const errorMessage = this.errorParsingService.extractMessage(error);
            alertServerError(errorText(), errorMessage);
        }
    }
}
